import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Space, Spin, message } from 'antd';
import { RobotOutlined, BulbOutlined, UserOutlined, RocketOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface SKUFormStrategyFieldsProps {
    skuId?: number;
    initialData?: any;
}

export const SKUFormStrategyFields: React.FC<SKUFormStrategyFieldsProps> = ({ skuId, initialData }) => {
    const [aiLoading, setAiLoading] = useState(false);
    const form = Form.useFormInstance();

    // 调用AI生成策略建议
    const handleAiGenerate = async () => {
        if (!skuId) {
            message.warning('请先保存SKU基本信息');
            return;
        }

        // 获取表单中的基础信息
        const basicInfo = form.getFieldsValue(['name', 'category', 'description', 'selling_points']);
        const { name, category, description, selling_points } = basicInfo;

        if (!name && !description) {
            message.warning('请至少填写SKU名称或描述，以便AI分析');
            return;
        }

        setAiLoading(true);
        try {
            // 模拟延迟，模拟AI思考过程
            await new Promise(resolve => setTimeout(resolve, 1500));

            // 基于实际输入信息生成的动态模拟数据
            // 在实际生产环境中，这里应该将 basicInfo 发送给后端 LLM 接口

            const sellingPointText = selling_points && selling_points.length > 0 ? selling_points.join('，') : '高品质';
            const categoryText = category || '该品类';

            const mockAiResponse = {
                target_audience: `针对${categoryText}的重度用户，特别是关注${sellingPointText.substring(0, 10)}...的人群。`,
                pain_points: `1. 市面上的${categoryText}普遍存在质量不稳定的问题。\n2. 用户经常抱怨${name || '同类产品'}的使用体验不够人性化。\n3. 缺乏针对${sellingPointText.substring(0, 5)}...场景的优化。`,
                usage_scenarios: `1. 在使用${name}进行日常操作时。\n2. 需要${sellingPointText.substring(0, 5)}...的特定场景下。\n3. 户外或移动办公环境。`,
                primary_usp: `${name}的核心优势在于其${sellingPointText}，解决了传统产品的痛点。`,
                differentiation: `相比竞品，${name}在${sellingPointText.substring(0, 8)}...方面做了深度优化，性能提升20%。`,
                market_positioning: `定位为${categoryText}领域的中高端产品，主打差异化体验。`,
                visual_style: `建议采用现代简约风格，突出${sellingPointText.substring(0, 5)}...的科技感和品质感。`
            };

            form.setFieldsValue(mockAiResponse);
            message.success('AI策略建议已根据产品信息生成！');
        } catch (error) {
            console.error('AI生成失败:', error);
            message.error('AI生成失败，请稍后重试');
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="sku-strategy-fields">
            <div style={{ marginBottom: 24, background: '#f0f5ff', padding: '16px', borderRadius: '8px', border: '1px solid #adc6ff' }}>
                <Space align="start">
                    <RobotOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                    <div>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#1d39c4' }}>AI 策略助手</div>
                        <div style={{ color: '#595959', marginTop: '4px' }}>
                            点击下方按钮，AI 将根据您的产品基本信息（名称、描述、类目），自动分析并生成目标人群、痛点、USP 等策略建议。
                        </div>
                        <Button
                            type="primary"
                            icon={aiLoading ? <Spin indicator={<ThunderboltOutlined spin />} /> : <ThunderboltOutlined />}
                            onClick={handleAiGenerate}
                            loading={aiLoading}
                            style={{ marginTop: '12px', background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)', border: 'none' }}
                        >
                            一键生成策略建议
                        </Button>
                    </div>
                </Space>
            </div>

            <div style={{ display: 'flex', marginBottom: 24 }}>
                <div style={{ width: 120, flexShrink: 0, fontWeight: 600, paddingTop: 6, marginRight: 16 }}>
                    <Space>
                        <UserOutlined /> 用户画像
                    </Space>
                </div>
                <div style={{ flex: 1 }}>
                    <Form.Item name="target_audience" label="目标人群 (Target Audience)" style={{ marginBottom: 12 }}>
                        <TextArea rows={2} placeholder="例如：25-35岁注重生活品质的职场女性..." />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="pain_points" label="用户痛点 (Pain Points)" style={{ marginBottom: 12 }}>
                                <TextArea rows={4} placeholder="用户在使用现有产品时遇到的主要问题..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="usage_scenarios" label="使用场景 (Usage Scenarios)" style={{ marginBottom: 12 }}>
                                <TextArea rows={4} placeholder="产品被使用的具体环境和情境..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </div>

            <div style={{ display: 'flex', marginBottom: 24 }}>
                <div style={{ width: 120, flexShrink: 0, fontWeight: 600, paddingTop: 6, marginRight: 16 }}>
                    <Space>
                        <RocketOutlined /> 核心竞争力
                    </Space>
                </div>
                <div style={{ flex: 1 }}>
                    <Form.Item name="primary_usp" label="核心卖点 (Primary USP)" style={{ marginBottom: 12 }}>
                        <Input placeholder="一句话概括产品的最大优势" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="differentiation" label="差异化优势 (Differentiation)" style={{ marginBottom: 12 }}>
                                <TextArea rows={3} placeholder="相比竞品的独特之处..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="market_positioning" label="市场定位 (Market Positioning)" style={{ marginBottom: 12 }}>
                                <Input placeholder="例如：高端、性价比、入门级..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </div>

            <div style={{ display: 'flex', marginBottom: 24 }}>
                <div style={{ width: 120, flexShrink: 0, fontWeight: 600, paddingTop: 6, marginRight: 16 }}>
                    <Space>
                        <BulbOutlined /> 视觉与风格
                    </Space>
                </div>
                <div style={{ flex: 1 }}>
                    <Form.Item name="visual_style" label="视觉风格建议" style={{ marginBottom: 12 }}>
                        <Input placeholder="例如：极简、科技感、复古、亲和力..." />
                    </Form.Item>
                </div>
            </div>
        </div>
    );
};
